import type { IClassificationRepository } from "@/application/ports/repositories";
import type {
	ClassificationResponse,
	ClassificationListResponse,
	ClassificationListParams,
	ClassificationSummaryResponse,
	SuppliersAnalyticsResponse,
	TaxBreakdownAnalyticsResponse,
	WorkforceAnalyticsResponse,
	CreateUploadSessionRequest,
	CreateUploadSessionResponse,
	UploadSessionStatusResponse,
	ReviewClassificationRequest,
	ClassificationFileResponse,
} from "@/domain/classification";
import { apiClient, ApiClientError } from "../client";
import { getLogger } from "@/infrastructure/di/services";

export class ClassificationRepository implements IClassificationRepository {
	private basePath(beneficiaryUuid: string): string {
		return `/beneficiaries/${beneficiaryUuid}/classifications`;
	}

	private buildUrl(path: string, params: URLSearchParams): string {
		const qs = params.toString();
		return qs ? `${path}?${qs}` : path;
	}

	private validateResponse<T>(data: unknown, requiredKeys: string[]): T {
		if (!data || typeof data !== "object") {
			throw new ApiClientError({
				message: "Invalid response format",
				status: 500,
				code: "INVALID_RESPONSE",
				details: { received: data },
			});
		}
		const missingKeys = requiredKeys.filter(
			(key) => !(key in (data as object)),
		);
		if (missingKeys.length > 0) {
			throw new ApiClientError({
				message: "Response missing required fields",
				status: 500,
				code: "INCOMPLETE_RESPONSE",
				details: { missingKeys, received: data },
			});
		}
		return data as T;
	}

	private handleError(error: unknown, operation: string): never {
		getLogger().error(`Classification ${operation} failed:`, error);
		if (error instanceof ApiClientError) throw error;
		if (error instanceof Error) {
			throw new ApiClientError({
				message: error.message || `Failed to ${operation}`,
				status: 0,
				code: "NETWORK_ERROR",
				details: { originalError: error.message },
			});
		}
		throw new ApiClientError({
			message: `An unexpected error occurred during ${operation}`,
			status: 0,
			code: "UNKNOWN_ERROR",
			details: { error: String(error) },
		});
	}

	async listClassifications(
		beneficiaryUuid: string,
		params?: ClassificationListParams,
	): Promise<ClassificationListResponse> {
		try {
			const queryParams = new URLSearchParams();
			if (params) {
				if (params.status) queryParams.append("status", params.status);
				if (params.document_type) {
					const types = Array.isArray(params.document_type)
						? params.document_type
						: [params.document_type];
					for (const dt of types) {
						queryParams.append("document_type", dt);
					}
				}
				if (params.search) queryParams.append("search", params.search);
				if (params.eligible !== undefined)
					queryParams.append("eligible", params.eligible.toString());
				if (params.expense_type)
					queryParams.append("expense_type", params.expense_type);
				if (params.fiscal_year !== undefined)
					queryParams.append("fiscal_year", params.fiscal_year.toString());
				if (params.limit !== undefined)
					queryParams.append("limit", params.limit.toString());
				if (params.offset !== undefined)
					queryParams.append("offset", params.offset.toString());
				if (params.sort_by) queryParams.append("sort_by", params.sort_by);
				if (params.sort_direction)
					queryParams.append("sort_direction", params.sort_direction);
			}

			const base = this.basePath(beneficiaryUuid);
			const url = this.buildUrl(base, queryParams);

			const response = await apiClient.get<ClassificationListResponse>(url);
			return this.validateResponse<ClassificationListResponse>(response.data, [
				"classifications",
				"total_count",
				"limit",
				"offset",
			]);
		} catch (error) {
			throw this.handleError(
				error,
				`list classifications for beneficiary ${beneficiaryUuid}`,
			);
		}
	}

	async getTypeCounts(
		beneficiaryUuid: string,
		params?: {
			status?: string;
			document_type?: string | string[];
			search?: string;
			eligible?: boolean;
			expense_type?: string;
			fiscal_year?: number;
		},
	): Promise<Record<string, number>> {
		try {
			const queryParams = new URLSearchParams();
			if (params?.status) queryParams.append("status", params.status);
			if (params?.document_type) {
				const types = Array.isArray(params.document_type)
					? params.document_type
					: [params.document_type];
				for (const dt of types) {
					queryParams.append("document_type", dt);
				}
			}
			if (params?.search) queryParams.append("search", params.search);
			if (params?.eligible !== undefined)
				queryParams.append("eligible", params.eligible.toString());
			if (params?.expense_type)
				queryParams.append("expense_type", params.expense_type);
			if (params?.fiscal_year !== undefined)
				queryParams.append("fiscal_year", params.fiscal_year.toString());

			const url = this.buildUrl(
				`${this.basePath(beneficiaryUuid)}/stats`,
				queryParams,
			);
			const response = await apiClient.get<Record<string, number>>(url);
			return response.data;
		} catch (error) {
			throw this.handleError(
				error,
				`get type counts for beneficiary ${beneficiaryUuid}`,
			);
		}
	}

	async getClassification(
		beneficiaryUuid: string,
		classificationUuid: string,
	): Promise<ClassificationResponse> {
		try {
			const response = await apiClient.get<ClassificationResponse>(
				`${this.basePath(beneficiaryUuid)}/${classificationUuid}`,
			);
			return this.validateResponse<ClassificationResponse>(response.data, [
				"uuid",
				"status",
				"source_filename",
			]);
		} catch (error) {
			throw this.handleError(error, `get classification ${classificationUuid}`);
		}
	}

	async getClassificationFile(
		beneficiaryUuid: string,
		classificationUuid: string,
	): Promise<ClassificationFileResponse> {
		try {
			const response = await apiClient.get<ClassificationFileResponse>(
				`${this.basePath(beneficiaryUuid)}/${classificationUuid}/file`,
			);
			return this.validateResponse<ClassificationFileResponse>(response.data, [
				"signed_url",
			]);
		} catch (error) {
			throw this.handleError(
				error,
				`get file for classification ${classificationUuid}`,
			);
		}
	}

	async getSummary(
		beneficiaryUuid: string,
		fiscalYear?: number,
		status?: string,
		documentType?: string,
		eligible?: boolean,
		expenseType?: string,
	): Promise<ClassificationSummaryResponse> {
		try {
			const queryParams = new URLSearchParams();
			if (fiscalYear !== undefined)
				queryParams.append("fiscal_year", fiscalYear.toString());
			if (status) queryParams.append("status", status);
			if (documentType) queryParams.append("document_type", documentType);
			if (eligible !== undefined)
				queryParams.append("eligible", eligible.toString());
			if (expenseType) queryParams.append("expense_type", expenseType);

			const base = `${this.basePath(beneficiaryUuid)}/summary`;
			const url = this.buildUrl(base, queryParams);

			const response = await apiClient.get<ClassificationSummaryResponse>(url);
			return this.validateResponse<ClassificationSummaryResponse>(
				response.data,
				[
					"financials",
					"document_inventory",
					"conciliation",
					"monthly",
					"categories",
				],
			);
		} catch (error) {
			throw this.handleError(
				error,
				`get summary for beneficiary ${beneficiaryUuid}`,
			);
		}
	}

	async getSuppliersAnalytics(
		beneficiaryUuid: string,
		fiscalYear?: number,
		expenseType?: string,
	): Promise<SuppliersAnalyticsResponse> {
		try {
			const queryParams = new URLSearchParams();
			if (fiscalYear !== undefined)
				queryParams.append("fiscal_year", fiscalYear.toString());
			if (expenseType) queryParams.append("expense_type", expenseType);

			const base = `${this.basePath(beneficiaryUuid)}/analytics/suppliers`;
			const url = this.buildUrl(base, queryParams);

			const response = await apiClient.get<SuppliersAnalyticsResponse>(url);
			return this.validateResponse<SuppliersAnalyticsResponse>(response.data, [
				"suppliers",
				"total_suppliers",
				"top3_concentration",
			]);
		} catch (error) {
			throw this.handleError(
				error,
				`get suppliers analytics for beneficiary ${beneficiaryUuid}`,
			);
		}
	}

	async getTaxBreakdown(
		beneficiaryUuid: string,
		fiscalYear?: number,
		expenseType?: string,
	): Promise<TaxBreakdownAnalyticsResponse> {
		try {
			const queryParams = new URLSearchParams();
			if (fiscalYear !== undefined)
				queryParams.append("fiscal_year", fiscalYear.toString());
			if (expenseType) queryParams.append("expense_type", expenseType);

			const base = `${this.basePath(beneficiaryUuid)}/analytics/tax-breakdown`;
			const url = this.buildUrl(base, queryParams);

			const response = await apiClient.get<TaxBreakdownAnalyticsResponse>(url);
			return this.validateResponse<TaxBreakdownAnalyticsResponse>(
				response.data,
				["total_iva", "total_irpf", "iva_by_rate"],
			);
		} catch (error) {
			throw this.handleError(
				error,
				`get tax breakdown for beneficiary ${beneficiaryUuid}`,
			);
		}
	}

	async getWorkforceAnalytics(
		beneficiaryUuid: string,
		fiscalYear?: number,
	): Promise<WorkforceAnalyticsResponse> {
		try {
			const queryParams = new URLSearchParams();
			if (fiscalYear !== undefined)
				queryParams.append("fiscal_year", fiscalYear.toString());

			const base = `${this.basePath(beneficiaryUuid)}/analytics/workforce`;
			const url = this.buildUrl(base, queryParams);

			const response = await apiClient.get<WorkforceAnalyticsResponse>(url);
			return this.validateResponse<WorkforceAnalyticsResponse>(response.data, [
				"total_payroll",
				"worker_count",
				"workers",
			]);
		} catch (error) {
			throw this.handleError(
				error,
				`get workforce analytics for beneficiary ${beneficiaryUuid}`,
			);
		}
	}

	async createUploadSession(
		beneficiaryUuid: string,
		data: CreateUploadSessionRequest,
	): Promise<CreateUploadSessionResponse> {
		try {
			const response = await apiClient.post<CreateUploadSessionResponse>(
				`${this.basePath(beneficiaryUuid)}/upload-session`,
				data,
				{ timeout: 60_000 },
			);
			return this.validateResponse<CreateUploadSessionResponse>(response.data, [
				"session_id",
				"expires_at",
				"uploads",
			]);
		} catch (error) {
			throw this.handleError(
				error,
				`create upload session for beneficiary ${beneficiaryUuid}`,
			);
		}
	}

	async getUploadSessionStatus(
		beneficiaryUuid: string,
		sessionUuid: string,
	): Promise<UploadSessionStatusResponse> {
		try {
			const response = await apiClient.get<UploadSessionStatusResponse>(
				`${this.basePath(beneficiaryUuid)}/sessions/${sessionUuid}`,
			);
			return this.validateResponse<UploadSessionStatusResponse>(response.data, [
				"session_id",
				"status",
				"expected_count",
			]);
		} catch (error) {
			throw this.handleError(error, `get upload session status ${sessionUuid}`);
		}
	}

	async updateClassification(
		beneficiaryUuid: string,
		classificationUuid: string,
		data: Record<string, unknown>,
	): Promise<ClassificationResponse> {
		try {
			const response = await apiClient.patch<ClassificationResponse>(
				`${this.basePath(beneficiaryUuid)}/${classificationUuid}`,
				data,
			);
			return this.validateResponse<ClassificationResponse>(response.data, [
				"uuid",
				"status",
				"source_filename",
			]);
		} catch (error) {
			throw this.handleError(
				error,
				`update classification ${classificationUuid}`,
			);
		}
	}

	async reviewClassification(
		beneficiaryUuid: string,
		classificationUuid: string,
		data: ReviewClassificationRequest,
	): Promise<ClassificationResponse> {
		try {
			const response = await apiClient.patch<ClassificationResponse>(
				`${this.basePath(beneficiaryUuid)}/${classificationUuid}/review`,
				data,
			);
			return this.validateResponse<ClassificationResponse>(response.data, [
				"uuid",
				"status",
				"source_filename",
			]);
		} catch (error) {
			throw this.handleError(
				error,
				`review classification ${classificationUuid}`,
			);
		}
	}

	async resolveDuplicate(
		beneficiaryUuid: string,
		data: { keep_uuid: string; discard_uuid: string },
	): Promise<{
		kept: ClassificationResponse;
		discarded: ClassificationResponse;
	}> {
		try {
			const response = await apiClient.post(
				`${this.basePath(beneficiaryUuid)}/resolve-duplicate`,
				data,
			);
			return this.validateResponse<{
				kept: ClassificationResponse;
				discarded: ClassificationResponse;
			}>(response.data, ["kept", "discarded"]);
		} catch (error) {
			throw this.handleError(error, "resolve duplicate");
		}
	}

	async deleteClassification(
		beneficiaryUuid: string,
		classificationUuid: string,
	): Promise<void> {
		try {
			await apiClient.delete(
				`${this.basePath(beneficiaryUuid)}/${classificationUuid}`,
			);
		} catch (error) {
			throw this.handleError(
				error,
				`delete classification ${classificationUuid}`,
			);
		}
	}

	async reclassifyClassification(
		beneficiaryUuid: string,
		classificationUuid: string,
	): Promise<ClassificationResponse> {
		try {
			const response = await apiClient.post<ClassificationResponse>(
				`${this.basePath(beneficiaryUuid)}/${classificationUuid}/reclassify`,
			);
			return this.validateResponse<ClassificationResponse>(response.data, [
				"uuid",
				"status",
			]);
		} catch (error) {
			throw this.handleError(
				error,
				`reclassify classification ${classificationUuid}`,
			);
		}
	}

	async downloadClassification(
		beneficiaryUuid: string,
		classificationUuid: string,
		filename: string,
	): Promise<void> {
		try {
			const fileResponse = await this.getClassificationFile(
				beneficiaryUuid,
				classificationUuid,
			);

			const response = await fetch(fileResponse.signed_url, {
				method: "GET",
				mode: "cors",
				credentials: "omit",
				headers: { Accept: "application/pdf,image/*" },
			});

			if (!response.ok) {
				throw new Error(`Download failed: ${response.status}`);
			}

			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = blobUrl;
			link.download = filename;
			link.style.display = "none";
			document.body.appendChild(link);
			link.click();

			setTimeout(() => {
				document.body.removeChild(link);
				window.URL.revokeObjectURL(blobUrl);
			}, 100);
		} catch (error) {
			throw this.handleError(
				error,
				`download classification ${classificationUuid}`,
			);
		}
	}
}
